import { FC } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useNavigate } from '@tanstack/react-location';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStoreWithSetters } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Avatar,
  NakedButton,
  PopoverMenu,
  PopoverMenuItem,
  Text,
  Truncated,
  usePopoverMenu,
} from '@frontend/design-system';
import { NWX_DASHBOARD_PATH, trackingIds } from '../../constants';

interface LocationSwitchProps {
  selectedLocationId: string;
  locationOptions: string[];
  onSignOut: () => void;
  containerStyles?: Interpolation<Theme>;
}
export const LocationSwitch: FC<LocationSwitchProps> = ({
  selectedLocationId,
  locationOptions,
  onSignOut,
  containerStyles,
}) => {
  const { getScopeName, setSelectedLocationIds, setSelectedGroupId, setSelectedOrgId } = useAppScopeStoreWithSetters();
  const navigate = useNavigate();
  const { t } = useTranslation('onboarding');
  const user = getUser();
  const userFullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

  const handleOpenWeaveClick = () => {
    setSelectedLocationIds([], { skipValidation: true });
    setSelectedGroupId('');
    setSelectedOrgId('');
    navigate({ to: NWX_DASHBOARD_PATH });
  };

  const swithLocation = (locationId: string) => {
    if (locationId === selectedLocationId) return;
    setSelectedLocationIds([locationId]);
  };

  const { getMenuProps, getTriggerProps, getItemProps } = usePopoverMenu({ placement: 'bottom-start' });

  return (
    <>
      <NakedButton
        {...getTriggerProps()}
        trackingId={trackingIds.onboardingV2.locationSwitch.menuClick}
        css={[styles.container, containerStyles]}
      >
        <div css={styles.locationInfo}>
          <Icon name='location-small' />
          <Text size='medium' weight='bold' css={{ maxWidth: '114px' }}>
            <Truncated>{getScopeName(selectedLocationId)}</Truncated>
          </Text>
        </div>

        <Avatar name={userFullName} size='small' />
      </NakedButton>
      <PopoverMenu {...getMenuProps()} css={{ maxWidth: 200 }}>
        {locationOptions.map((locationId) => (
          <PopoverMenuItem
            key={locationId}
            {...getItemProps()}
            active={locationId === selectedLocationId}
            onClick={() => swithLocation(locationId)}
            hoverLabel={getScopeName(locationId)}
            trackingId={trackingIds.onboardingV2.locationSwitch.locationClick}
            Icon={() => <Icon name='location-small' color='subdued' />}
          >
            {getScopeName(locationId)}
          </PopoverMenuItem>
        ))}
        <PopoverMenuItem
          {...getItemProps()}
          onClick={handleOpenWeaveClick}
          trackingId={trackingIds.onboardingV2.locationSwitch.openWeaveClick}
          Icon={() => <Icon name='weave-logo-small' color='subdued' />}
        >
          {t('Open Weave')}
        </PopoverMenuItem>
        <PopoverMenuItem
          {...getItemProps()}
          onClick={onSignOut}
          trackingId={trackingIds.onboardingV2.locationSwitch.signOutClick}
          Icon={() => <Icon name='log-out-small' color='subdued' />}
        >
          {t('Sign Out')}
        </PopoverMenuItem>
      </PopoverMenu>
    </>
  );
};

const styles = {
  container: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
  locationInfo: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    max-width: 114px;
  `,
};
